import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import OtpInput from "react-otp-input";
import { useDispatch } from "react-redux";
import { $user } from "../helper/UserFactory";
import { twoFactorRequest, methodLabel } from "../helper/twoFactorApi";
import LanguageContext from "../helper/LanguageContext";
import { RIGHT_TOP, SET_OPEN_SIDE_BAR, SET_USER_LOGIN_TOKEN } from "../store";
import { queueStartupNavigation, isDeeplinkPath } from "../utils/startupScreen";
import "../styles/pages.css";
import "./TwoFactorChallenge.css";

const OTP_INPUT_STYLE = {
  height: 48,
  width: 40,
  margin: "0 4px",
  fontSize: 20,
  fontWeight: 600,
  color: "#303030",
  border: "1.5px solid #d9d9d9",
  borderRadius: 10,
  outline: "none",
  textAlign: "center",
};

function TwoFactorChallenge() {
  const { strings } = React.useContext(LanguageContext);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const challenge = (location.state && location.state.challenge) || {};
  const [challengeToken, setChallengeToken] = useState(challenge.challengeToken || "");
  const [method, setMethod] = useState(challenge.defaultMethod || "totp");
  const [methods, setMethods] = useState((challenge.methods || []).filter((item) => item.type !== "sms"));
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [showMethods, setShowMethods] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(challenge.defaultMethod === "totp" ? 0 : 60);
  const submitting = useRef(false);

  const isWebView = typeof window !== "undefined" && window.ReactNativeWebView;
  const sendWebViewMessage = useCallback((message) => {
    if (isWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    }
  }, [isWebView]);

  useEffect(() => {
    document.body.classList.add("login-page-active");
    return () => document.body.classList.remove("login-page-active");
  }, []);

  useEffect(() => {
    if (!challengeToken) {
      history.replace("/");
    }
  }, [challengeToken, history]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    document.querySelectorAll(".twofa-otp-row input").forEach((el) => {
      el.setAttribute("autocomplete", "one-time-code");
      el.setAttribute("inputmode", "numeric");
    });
  }, []);

  const currentHint = useMemo(
    () => ((methods.find((item) => item.type === method) || {}).hint || ""),
    [methods, method]
  );

  const finishLogin = useCallback((res) => {
    if (!$user.persistLoginSession(res)) {
      setError(strings.twofa_something_wrong || "Something went wrong. Please try again.");
      return;
    }
    sendWebViewMessage({
      type: "LOGIN_RESULT",
      data: {
        success: true,
        requires2FA: false,
        userId: res.user && res.user.id,
        userLanguage: res.user && res.user.language,
        timestamp: new Date().toISOString(),
      },
    });
    const startup = queueStartupNavigation(res.user && res.user.startupScreen);
    dispatch({ type: SET_OPEN_SIDE_BAR, payload: startup.sidebar });
    if (!isDeeplinkPath(startup.path)) {
      dispatch({ type: RIGHT_TOP, rightTop: "" });
    }
    dispatch({ type: SET_USER_LOGIN_TOKEN, status: res.token });
  }, [dispatch, sendWebViewMessage, strings]);

  const submitCode = useCallback(async (value) => {
    const next = String(value || code || "").trim();
    if (next.length < 6 || submitting.current || !challengeToken) return;
    submitting.current = true;
    setLoading(true);
    setError("");
    const res = await twoFactorRequest("post", "/auth/2fa/verify", {
      challengeToken,
      code: next,
      rememberDevice,
    });
    setLoading(false);
    submitting.current = false;
    if (res && res.status && res.token) {
      finishLogin(res);
      return;
    }
    setError(res && res.message ? res.message : (strings.twofa_invalid_code || "That code is incorrect. Please try again."));
    setCode("");
    if (res && (res.reason === "expired_or_invalid" || res.reason === "too_many_attempts")) {
      history.replace("/");
    }
  }, [challengeToken, code, rememberDevice, finishLogin, history, strings]);

  const onResend = async () => {
    if (cooldown > 0 || method === "totp") return;
    const res = await twoFactorRequest("post", "/auth/2fa/resend", { challengeToken });
    if (res && res.status) {
      setInfo(res.message || strings.twofa_sent || "We sent a code.");
      setError("");
      setCooldown(60);
    } else {
      setError((res && res.message) || strings.twofa_something_wrong || "Something went wrong. Please try again.");
    }
  };

  const onSwitch = async (nextMethod) => {
    if (nextMethod === method) {
      setShowMethods(false);
      return;
    }
    const res = await twoFactorRequest("post", "/auth/2fa/method", { challengeToken, method: nextMethod });
    if (!res || !res.status) {
      setError((res && res.message) || strings.twofa_method_unavailable || "That verification method is not available.");
      return;
    }
    if (res.challengeToken) setChallengeToken(res.challengeToken);
    setMethod(res.method || nextMethod);
    if (res.methods) setMethods(res.methods.filter((item) => item.type !== "sms"));
    setShowMethods(false);
    setCode("");
    setError("");
    setInfo(res.message || strings.twofa_sent || "We sent a code.");
    setCooldown(nextMethod === "totp" ? 0 : 60);
  };

  const lead = method === "totp"
    ? (strings.twofa_enter_app || "Enter the 6-digit code from your authenticator app.")
    : method === "sms"
      ? (strings.twofa_enter_sms || "Enter the 6-digit code we sent by text message.")
      : (strings.twofa_enter_email || "Enter the 6-digit code we sent to your email.");

  return (
    <div className="login-page">
      <div className="login-container">
        <img
          className="login-logo"
          src={process.env.PUBLIC_URL + "/assets/img/KJUUP-Schrift-Icon.png"}
          alt="KJUUP Logo"
        />
        <h2 className="login-title">{strings.twofa_title || "Two-factor authentication"}</h2>
        <p className="twofa-lead">{lead}</p>
        {currentHint ? <p className="twofa-hint">{currentHint}</p> : null}
        {info ? <p className="twofa-info">{info}</p> : null}
        {error ? <p className="text-danger twofa-error">{error}</p> : null}
        <div className="twofa-otp-row">
          <OtpInput
            value={code}
            onChange={(value) => {
              const next = String(value || "").replace(/\D/g, "").slice(0, 6);
              setCode(next);
              if (next.length === 6) submitCode(next);
            }}
            numInputs={6}
            isInputNum
            shouldAutoFocus
            separator={<span className="two-factor-challenge__separator" />}
            inputStyle={OTP_INPUT_STYLE}
          />
        </div>
        <button
          type="button"
          className="kj-btn kj-btn--outline kj-btn--block twofa-confirm"
          disabled={loading || code.length !== 6}
          onClick={() => submitCode(code)}
        >
          {loading ? <Spinner animation="border" size="sm" /> : (strings.confirm || "Confirm")}
        </button>
        {method !== "totp" ? (
          <button
            type="button"
            className="kj-btn kj-btn--ghost kj-btn--sm"
            disabled={cooldown > 0}
            onClick={onResend}
          >
            {cooldown > 0
              ? `${strings.twofa_resend || "Resend code"} (${cooldown})`
              : (strings.twofa_resend || "Resend code")}
          </button>
        ) : null}
        {methods.length > 1 ? (
          <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm" onClick={() => setShowMethods((v) => !v)}>
            {strings.twofa_try_another || "Try another method"}
          </button>
        ) : null}
        {showMethods
          ? methods.map((item) => (
              <button
                type="button"
                key={item.type}
                className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--block"
                onClick={() => onSwitch(item.type)}
              >
                {methodLabel(item.type, strings)}{item.hint ? `  ${item.hint}` : ""}
              </button>
            ))
          : null}
        <Form.Check
          className="twofa-remember"
          type="checkbox"
          id="twofa-remember-device"
          checked={rememberDevice}
          onChange={(e) => setRememberDevice(e.target.checked)}
          label={strings.twofa_remember_device || "Don't ask again on this device for 30 days"}
        />
        <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm" onClick={() => history.replace("/")}>
          {strings.back || "Back"}
        </button>
      </div>
    </div>
  );
}

export default TwoFactorChallenge;
